import { setDocumentPageTitle } from "@repo/ui/documentTitle/documentTitle";
import { useEffect, type RefObject } from "react";
import { selectCompany } from "../../features/company/companySlice.js";
import { selectIsServiceSession } from "../../features/teammate/teammateSlice.js";
import { getWorkspaceAlias } from "../../helpers/workspaceAlias.js";
import { useAppSelector } from "../../hooks/hook.js";

/**
 * The browser-tab title: the page's own title, then the workspace alias in a
 * service access session, then the product.
 */
export function buildDocumentTitle(
  pageTitle: string,
  workspaceAlias: string | null,
) {
  if (!pageTitle) {
    return "Alpharun";
  }
  return workspaceAlias
    ? `${pageTitle} | ${workspaceAlias} | Alpharun`
    : `${pageTitle} | Alpharun`;
}

/**
 * Keeps the page title on the current breadcrumb's text. It is read from the
 * trail rendered under `trailRef`, so an entity name counts once its crumb
 * label has loaded.
 */
export function useBreadcrumbDocumentTitle(
  trailRef: RefObject<HTMLElement | null>,
) {
  const company = useAppSelector(selectCompany);
  const isServiceSession = useAppSelector(selectIsServiceSession);
  const workspaceAlias =
    isServiceSession && company ? getWorkspaceAlias(company.id) : null;

  useEffect(() => {
    const trail = trailRef.current;
    if (!trail) {
      return;
    }
    const updateTitle = () => {
      const currentCrumb = trail.querySelector('[aria-current="page"]');
      const pageTitle = currentCrumb?.textContent?.trim() ?? "";
      setDocumentPageTitle(buildDocumentTitle(pageTitle, workspaceAlias));
    };
    updateTitle();
    const observer = new MutationObserver(updateTitle);
    observer.observe(trail, {
      subtree: true,
      childList: true,
      characterData: true,
      attributeFilter: ["aria-current"],
    });
    return () => {
      observer.disconnect();
      setDocumentPageTitle(buildDocumentTitle("", workspaceAlias));
    };
  }, [trailRef, workspaceAlias]);
}
